import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API });

const TOKEN_KEY = "edoc.access_token";
const REFRESH_KEY = "edoc.refresh_token";
const ORG_KEY = "edoc.current_org_id";

export function getAccessToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

export function getRefreshToken() {
  try {
    return localStorage.getItem(REFRESH_KEY) || null;
  } catch {
    return null;
  }
}

export function setTokens({ access_token, refresh_token }) {
  try {
    if (access_token) localStorage.setItem(TOKEN_KEY, access_token);
    if (refresh_token) localStorage.setItem(REFRESH_KEY, refresh_token);
  } catch {}
}

export function clearTokens() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch {}
}

export function getCurrentOrgId() {
  try {
    return localStorage.getItem(ORG_KEY) || null;
  } catch {
    return null;
  }
}

export function setCurrentOrgId(orgId) {
  try {
    if (orgId) localStorage.setItem(ORG_KEY, orgId);
    else localStorage.removeItem(ORG_KEY);
  } catch {}
}

client.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Skip X-Org-Id on auth, me, public, and invitation-accept routes
  const url = config.url || "";
  const skipOrgHeader =
    url.startsWith("/auth") ||
    url.startsWith("/me") ||
    url.startsWith("/public") ||
    url.startsWith("/invitations/accept");
  if (!skipOrgHeader) {
    const orgId = getCurrentOrgId();
    if (orgId) {
      config.headers = config.headers || {};
      config.headers["X-Org-Id"] = orgId;
    }
  }
  return config;
});

let refreshInFlight = null;

async function attemptRefresh() {
  const refresh_token = getRefreshToken();
  if (!refresh_token) return null;
  if (!refreshInFlight) {
    refreshInFlight = axios
      .post(`${API}/auth/refresh`, { refresh_token })
      .then((r) => {
        setTokens(r.data.tokens);
        return r.data.tokens.access_token;
      })
      .catch(() => {
        clearTokens();
        return null;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

client.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const isAuthEndpoint = (original.url || "").startsWith("/auth");
    if (status === 401 && !original.__retried && !isAuthEndpoint) {
      original.__retried = true;
      const newAccess = await attemptRefresh();
      if (newAccess) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newAccess}`;
        return client.request(original);
      }
    }
    return Promise.reject(error);
  },
);

export { client };

export const api = {
  // auth
  signup: (data) => client.post("/auth/signup", data).then((r) => r.data),
  // Включён ли барьер подтверждения email. Пока почта на сервере не настроена,
  // барьер выключен, и экран ввода кода показывать нельзя — иначе регистрация
  // упрётся в код, который некому отправить.
  signupMode: () => client.get("/auth/signup/mode").then((r) => r.data),
  // Подтверждение адреса до регистрации: запросить код и обменять его на
  // proof-токен, без которого /auth/signup отвечает EMAIL_NOT_VERIFIED.
  requestSignupCode: (email) =>
    client.post("/auth/signup/request-code", { email }).then((r) => r.data),
  verifySignupCode: (email, code) =>
    client.post("/auth/signup/verify-code", { email, code }).then((r) => r.data),
  login: (data) => client.post("/auth/login", data).then((r) => r.data),
  ecpLoginOrRegister: (data) =>
    client.post("/auth/ecp/login-or-register", data).then((r) => r.data),
  ecpMode: () => client.get("/auth/ecp/mode").then((r) => r.data),
  ecpChallenge: () =>
    client.post("/auth/ecp/challenge").then((r) => r.data),
  refresh: (refresh_token) =>
    client.post("/auth/refresh", { refresh_token }).then((r) => r.data),
  logout: (refresh_token) =>
    client.post("/auth/logout", { refresh_token }).then((r) => r.data),
  verifyEmail: (token) =>
    client.post("/auth/verify-email", { token }).then((r) => r.data),
  forgotPassword: (email) =>
    client.post("/auth/forgot-password", { email }).then((r) => r.data),
  resetPassword: (token, password) =>
    client.post("/auth/reset-password", { token, password }).then((r) => r.data),

  // me + orgs + members + invitations
  me: () => client.get("/me").then((r) => r.data),
  setLocale: (locale) =>
    client.patch("/me/locale", { locale }).then((r) => r.data),
  getOrg: (orgId) => client.get(`/orgs/${orgId}`).then((r) => r.data),
  updateOrg: (orgId, data) =>
    client.put(`/orgs/${orgId}`, data).then((r) => r.data),
  uploadOrgLogo: (orgId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client
      .post(`/orgs/${orgId}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },
  // Документы компании (Phase OC) — org берётся из X-Org-Id (интерцептор).
  listCompanyDocuments: (category) =>
    client
      .get("/company-documents", { params: category ? { category } : {} })
      .then((r) => r.data),
  uploadCompanyDocument: (file, title, category) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title);
    if (category) fd.append("category", category);
    return client
      .post("/company-documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },
  updateCompanyDocument: (id, data) =>
    client.put(`/company-documents/${id}`, data).then((r) => r.data),
  deleteCompanyDocument: (id) =>
    client.delete(`/company-documents/${id}`).then((r) => r.data),
  downloadCompanyDocument: (id) =>
    client
      .get(`/company-documents/${id}/file`, { responseType: "blob" })
      .then((r) => r.data),
  // Каталог / Active Directory (Phase OD).
  getDirectory: (orgId) =>
    client.get(`/orgs/${orgId}/directory`).then((r) => r.data),
  updateDirectory: (orgId, data) =>
    client.put(`/orgs/${orgId}/directory`, data).then((r) => r.data),
  syncDirectory: (orgId) =>
    client.post(`/orgs/${orgId}/directory/sync`).then((r) => r.data),
  listDirectoryRuns: (orgId) =>
    client.get(`/orgs/${orgId}/directory/runs`).then((r) => r.data),
  listMembers: (orgId) =>
    client.get(`/orgs/${orgId}/members`).then((r) => r.data),
  updateMemberRole: (orgId, userId, role) =>
    client.put(`/orgs/${orgId}/members/${userId}`, { role }).then((r) => r.data),
  setMemberStatus: (orgId, userId, status) =>
    client
      .put(`/orgs/${orgId}/members/${userId}/status`, { status })
      .then((r) => r.data),
  transferOwnership: (orgId, userId, demoteTo = "admin") =>
    client
      .post(`/orgs/${orgId}/transfer-ownership`, {
        user_id: userId,
        demote_to: demoteTo,
      })
      .then((r) => r.data),
  removeMember: (orgId, userId) =>
    client.delete(`/orgs/${orgId}/members/${userId}`).then((r) => r.data),
  createInvitation: (orgId, data) =>
    client.post(`/orgs/${orgId}/invitations`, data).then((r) => r.data),
  listInvitations: (orgId) =>
    client.get(`/orgs/${orgId}/invitations`).then((r) => r.data),
  revokeInvitation: (orgId, invitationId) =>
    client
      .delete(`/orgs/${orgId}/invitations/${invitationId}`)
      .then((r) => r.data),
  acceptInvitation: (token) =>
    client.post("/invitations/accept", { token }).then((r) => r.data),
  // ЭЦП-заявки на вступление (Phase T)
  listJoinRequests: (orgId, status = "pending") =>
    client
      .get(`/orgs/${orgId}/join-requests`, { params: { status_filter: status } })
      .then((r) => r.data),
  approveJoinRequest: (orgId, requestId, role) =>
    client
      .post(`/orgs/${orgId}/join-requests/${requestId}/approve`, { role })
      .then((r) => r.data),
  rejectJoinRequest: (orgId, requestId) =>
    client
      .post(`/orgs/${orgId}/join-requests/${requestId}/reject`)
      .then((r) => r.data),

  // contractors
  listContractors: () => client.get("/contractors").then((r) => r.data),
  getContractor: (id) => client.get(`/contractors/${id}`).then((r) => r.data),
  createContractor: (data) =>
    client.post("/contractors", data).then((r) => r.data),
  updateContractor: (id, data) =>
    client.put(`/contractors/${id}`, data).then((r) => r.data),
  deleteContractor: (id) =>
    client.delete(`/contractors/${id}`).then((r) => r.data),
  verifyContractor: (id) =>
    client.post(`/contractors/${id}/verify`).then((r) => r.data),
  // Благонадёжность по сырому БИН (для подписантов без карточки контрагента).
  checkAdataBin: (bin) =>
    client.post("/adata/check", { bin }).then((r) => r.data),

  // Канцелярия / ЕСЭДО (Phase V)
  listIncoming: (params = {}) =>
    client.get("/correspondence/incoming", { params }).then((r) => r.data),
  listOutgoing: (params = {}) =>
    client.get("/correspondence/outgoing", { params }).then((r) => r.data),
  getCorrespondence: (id) =>
    client.get(`/correspondence/${id}`).then((r) => r.data),
  sendCorrespondence: (data) =>
    client.post("/correspondence/send", data).then((r) => r.data),
  markCorrespondenceRead: (id) =>
    client.post(`/correspondence/${id}/read`).then((r) => r.data),
  acceptCorrespondence: (id) =>
    client.post(`/correspondence/${id}/accept`).then((r) => r.data),
  rejectCorrespondence: (id) =>
    client.post(`/correspondence/${id}/reject`).then((r) => r.data),
  replyCorrespondence: (id, data) =>
    client.post(`/correspondence/${id}/reply`, data).then((r) => r.data),
  closeCorrespondence: (id) =>
    client.post(`/correspondence/${id}/close`).then((r) => r.data),

  // Phase W — резолюции и поручения
  addResolution: (correspondenceId, data) =>
    client
      .post(`/correspondence/${correspondenceId}/resolution`, data)
      .then((r) => r.data),
  listLetterInstructions: (correspondenceId) =>
    client
      .get(`/correspondence/${correspondenceId}/instructions`)
      .then((r) => r.data),
  listInstructions: (params = {}) =>
    client.get("/instructions", { params }).then((r) => r.data),
  startInstruction: (id) =>
    client.post(`/instructions/${id}/start`).then((r) => r.data),
  completeInstruction: (id, report) =>
    client.post(`/instructions/${id}/complete`, { report }).then((r) => r.data),
  cancelInstruction: (id) =>
    client.post(`/instructions/${id}/cancel`).then((r) => r.data),

  // Phase X — номенклатура дел + маршруты согласования
  listCases: (params = {}) =>
    client.get("/cases", { params }).then((r) => r.data),
  createCase: (data) => client.post("/cases", data).then((r) => r.data),
  updateCase: (id, data) => client.put(`/cases/${id}`, data).then((r) => r.data),
  deleteCase: (id) => client.delete(`/cases/${id}`).then((r) => r.data),
  fileCorrespondence: (id, caseId) =>
    client.post(`/correspondence/${id}/file`, { case_id: caseId }).then((r) => r.data),
  createCorrespondenceDraft: (data) =>
    client.post("/correspondence/draft", data).then((r) => r.data),
  submitCorrespondence: (id) =>
    client.post(`/correspondence/${id}/submit`).then((r) => r.data),
  createApprovalRoute: (id, steps) =>
    client.post(`/correspondence/${id}/approval-route`, { steps }).then((r) => r.data),
  getApprovalRoute: (id) =>
    client.get(`/correspondence/${id}/approval-route`).then((r) => r.data),
  approveApprovalStep: (id, stepId) =>
    client
      .post(`/correspondence/${id}/approval-steps/${stepId}/approve`)
      .then((r) => r.data),
  returnApprovalStep: (id, stepId, remark) =>
    client
      .post(`/correspondence/${id}/approval-steps/${stepId}/return`, { remark })
      .then((r) => r.data),

  // templates
  listTemplates: () => client.get("/templates").then((r) => r.data),
  getTemplate: (id) => client.get(`/templates/${id}`).then((r) => r.data),
  createTemplate: (data) =>
    client.post("/templates", data).then((r) => r.data),
  updateTemplate: (id, data) =>
    client.put(`/templates/${id}`, data).then((r) => r.data),
  deleteTemplate: (id) => client.delete(`/templates/${id}`).then((r) => r.data),
  getTemplatePdfUrl: (id) => `${API}/templates/${id}/pdf`,

  // documents
  listDocuments: (status) =>
    client
      .get("/documents", { params: status ? { status } : {} })
      .then((r) => r.data),
  getDocument: (id) => client.get(`/documents/${id}`).then((r) => r.data),
  uploadDocument: (formData) =>
    client
      .post("/documents/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data),
  updateDocument: (id, data) =>
    client.put(`/documents/${id}`, data).then((r) => r.data),
  sendDocument: (id, data = {}) =>
    client.post(`/documents/${id}/send`, data).then((r) => r.data),
  voidDocument: (id, reason) =>
    client.post(`/documents/${id}/void`, { reason }).then((r) => r.data),
  declineDocument: (id, recipientId, reason) =>
    client
      .post(`/documents/${id}/decline`, {
        recipient_id: recipientId,
        reason,
      })
      .then((r) => r.data),
  // --- оргструктура: подразделения, должности -------------------------------
  listDepartments: () => client.get("/departments").then((r) => r.data),
  departmentTree: () => client.get("/departments/tree").then((r) => r.data),
  createDepartment: (data) => client.post("/departments", data).then((r) => r.data),
  updateDepartment: (id, data) =>
    client.patch(`/departments/${id}`, data).then((r) => r.data),
  deleteDepartment: (id) => client.delete(`/departments/${id}`).then((r) => r.data),
  listPositions: () => client.get("/org-structure/positions").then((r) => r.data),
  // Присланные поля меняются, отсутствующие — нет: перевод в другой отдел не
  // должен стирать должность.
  setMemberPlacement: (userId, data) =>
    client.put(`/org-structure/members/${userId}`, data).then((r) => r.data),

  // --- виды документов и маршруты согласования -------------------------------
  listDocumentTypes: () => client.get("/document-types").then((r) => r.data),
  createDocumentType: (data) =>
    client.post("/document-types", data).then((r) => r.data),
  updateDocumentType: (id, data) =>
    client.patch(`/document-types/${id}`, data).then((r) => r.data),
  deleteDocumentType: (id) =>
    client.delete(`/document-types/${id}`).then((r) => r.data),
  // Стандартный набор видов (служебная записка, приказ, заявление…) вместе с
  // готовыми бланками. Идемпотентно: уже заведённые виды попадают в skipped.
  seedStandardDocumentTypes: () =>
    client.post("/document-types/seed-standard").then((r) => r.data),

  // Форма реквизитов вида: поля = {{плейсхолдеры}} бланка, подписи — из вида.
  documentTypeForm: (typeId) =>
    client.get(`/document-types/${typeId}/form`).then((r) => r.data),
  createDocumentFromType: (typeId, data) =>
    client.post(`/document-types/${typeId}/documents`, data).then((r) => r.data),

  listApprovalRoutes: () => client.get("/approval-routes").then((r) => r.data),
  getApprovalRoute: (id) => client.get(`/approval-routes/${id}`).then((r) => r.data),
  createApprovalRoute: (data) =>
    client.post("/approval-routes", data).then((r) => r.data),
  updateApprovalRoute: (id, data) =>
    client.patch(`/approval-routes/${id}`, data).then((r) => r.data),
  deleteApprovalRoute: (id) =>
    client.delete(`/approval-routes/${id}`).then((r) => r.data),

  // Кто окажется в маршруте для ЭТОГО документа — показываем до отправки.
  approvalPreview: (documentId, routeId) =>
    client
      .get(`/documents/${documentId}/approval-preview`, {
        params: routeId ? { route_id: routeId } : undefined,
      })
      .then((r) => r.data),
  submitForApproval: (documentId, routeId) =>
    client
      .post(`/documents/${documentId}/submit-for-approval`,
        routeId ? { route_id: routeId } : {})
      .then((r) => r.data),

  signDocument: (id, data) =>
    client.post(`/documents/${id}/sign`, data).then((r) => r.data),
  // Real ЭЦП (NCALayer): canonical bytes to sign + ncanode/mock flags
  getSignData: (id) =>
    client.get(`/documents/${id}/sign-data`).then((r) => r.data),
  publicSignData: (docId, recipientId, token) =>
    axios
      .get(
        `${API}/public/documents/${docId}/recipients/${recipientId}/sign-data`,
        { params: { t: token } },
      )
      .then((r) => r.data),
  bulkSignDocuments: (data) =>
    client.post("/documents/bulk-sign", data).then((r) => r.data),
  deleteDocument: (id) =>
    client.delete(`/documents/${id}`).then((r) => r.data),
  generateFromTemplate: (data) =>
    client.post("/documents/generate-from-template", data).then((r) => r.data),
  generateAndSend: (templateId, data) =>
    client
      .post(`/templates/${templateId}/generate-and-send`, data)
      .then((r) => r.data),
  getPdfUrl: (id) => `${API}/documents/${id}/pdf`,
  fetchPdfBlob: (id) =>
    client
      .get(`/documents/${id}/pdf`, { responseType: "blob" })
      .then((r) => r.data),

  // document chat (переписка с контрагентом) — authed (org member)
  listDocumentMessages: (docId) =>
    client.get(`/documents/${docId}/messages`).then((r) => r.data),
  postDocumentMessage: (docId, body, channel = "external") =>
    client.post(`/documents/${docId}/messages`, { body, channel }).then((r) => r.data),
  // document chat — public (external signer, token in query string)
  publicListMessages: (docId, recipientId, token) =>
    axios
      .get(`${API}/public/documents/${docId}/recipients/${recipientId}/messages`, {
        params: { t: token },
      })
      .then((r) => r.data),
  publicPostMessage: (docId, recipientId, token, body) =>
    axios
      .post(
        `${API}/public/documents/${docId}/recipients/${recipientId}/messages`,
        { body },
        { params: { t: token } },
      )
      .then((r) => r.data),

  // Phase D — anchors + per-recipient sign methods
  saveAnchors: (id, anchors) =>
    client
      .put(`/documents/${id}`, { signature_anchors: anchors })
      .then((r) => r.data),
  saveRecipientMethods: (id, recipients) =>
    client.put(`/documents/${id}`, { recipients }).then((r) => r.data),
  // Owner/admin recovery: re-stamp signed fields + certificate from the immutable
  // original for a document whose sign-time PDF render failed (sidecar was down).
  rerenderDocument: (id) =>
    client.post(`/documents/${id}/rerender`).then((r) => r.data),

  // Phase D — public portal (no auth, token in query string)
  publicGetDocument: (docId, recipientId, token) =>
    axios
      .get(
        `${API}/public/documents/${docId}/recipients/${recipientId}`,
        { params: { t: token } },
      )
      .then((r) => r.data),
  publicPdfUrl: (docId, recipientId, token) =>
    `${API}/public/documents/${docId}/recipients/${recipientId}/pdf?t=${encodeURIComponent(token)}`,
  publicDecline: (docId, recipientId, token, reason) =>
    client
      .post(
        `/public/documents/${docId}/recipients/${recipientId}/decline`,
        { reason },
        { params: { t: token } },
      )
      .then((r) => r.data),
  publicSignEcp: (docId, recipientId, token, data) =>
    axios
      .post(
        `${API}/public/documents/${docId}/recipients/${recipientId}/sign-ecp`,
        data,
        { params: { t: token } },
      )
      .then((r) => r.data),

  // forms (Phase C)
  listForms: (includeArchived = false) =>
    client
      .get("/forms", { params: includeArchived ? { include_archived: true } : {} })
      .then((r) => r.data),
  getForm: (id) => client.get(`/forms/${id}`).then((r) => r.data),
  createForm: (data) => client.post("/forms", data).then((r) => r.data),
  updateForm: (id, data) => client.put(`/forms/${id}`, data).then((r) => r.data),
  deleteForm: (id) => client.delete(`/forms/${id}`).then((r) => r.data),
  submitForm: (id, data) =>
    client.post(`/forms/${id}/submissions`, { data }).then((r) => r.data),
  listSubmissions: (id) =>
    client.get(`/forms/${id}/submissions`).then((r) => r.data),

  // tasks (Phase E)
  listTasks: (params = {}) =>
    client.get("/tasks", { params }).then((r) => r.data),
  countTasks: () => client.get("/tasks/count").then((r) => r.data),
  getTask: (id) => client.get(`/tasks/${id}`).then((r) => r.data),
  claimTask: (id) => client.post(`/tasks/${id}/claim`).then((r) => r.data),
  completeTask: (id, data) =>
    client.post(`/tasks/${id}/complete`, data).then((r) => r.data),

  // workflows (Phase F)
  listWorkflows: (includeArchived = false) =>
    client
      .get("/workflows", {
        params: includeArchived ? { include_archived: true } : {},
      })
      .then((r) => r.data),
  getWorkflow: (id) => client.get(`/workflows/${id}`).then((r) => r.data),
  createWorkflow: (data) =>
    client.post("/workflows", data).then((r) => r.data),
  updateWorkflow: (id, data) =>
    client.put(`/workflows/${id}`, data).then((r) => r.data),
  deleteWorkflow: (id) =>
    client.delete(`/workflows/${id}`).then((r) => r.data),
  publishWorkflow: (id) =>
    client.post(`/workflows/${id}/publish`).then((r) => r.data),
  validateWorkflow: (id, graph) =>
    client
      .post(`/workflows/${id}/validate`, graph !== undefined ? { graph } : {})
      .then((r) => r.data),
  listWorkflowVersions: (id) =>
    client.get(`/workflows/${id}/versions`).then((r) => r.data),

  // workflow engine (Phase G)
  startWorkflowInstance: (workflowId, body = {}) =>
    client
      .post(`/workflows/${workflowId}/start`, body)
      .then((r) => r.data),
  workflowRunnable: (workflowId) =>
    client.get(`/workflows/${workflowId}/runnable`).then((r) => r.data),
  listWorkflowInstances: (params = {}) =>
    client.get("/workflow-instances", { params }).then((r) => r.data),
  getWorkflowInstance: (instanceId) =>
    client.get(`/workflow-instances/${instanceId}`).then((r) => r.data),
  terminateWorkflowInstance: (instanceId, reason) =>
    client
      .post(`/workflow-instances/${instanceId}/terminate`, { reason })
      .then((r) => r.data),
  retryWorkflowInstance: (instanceId) =>
    client
      .post(`/workflow-instances/${instanceId}/retry`)
      .then((r) => r.data),

  // notifications (Phase H)
  listNotifications: (params = {}) =>
    client.get("/notifications", { params }).then((r) => r.data),
  countNotifications: () =>
    client.get("/notifications/count").then((r) => r.data),
  markNotificationRead: (id) =>
    client.post(`/notifications/${id}/read`).then((r) => r.data),
  markAllNotificationsRead: () =>
    client.post("/notifications/read-all").then((r) => r.data),
  getNotificationPrefs: () =>
    client.get("/me/notification-prefs").then((r) => r.data),
  updateNotificationPrefs: (prefs) =>
    client.patch("/me/notification-prefs", prefs).then((r) => r.data),

  // template library + composer (Phase N)
  listTemplateLibrary: () =>
    client.get("/template-library").then((r) => r.data),
  importTemplateLibrary: (ids) =>
    client
      .post("/template-library/import", ids ? { ids } : {})
      .then((r) => r.data),
  getLibrarySections: (libraryId) =>
    client.get(`/template-library/${libraryId}/sections`).then((r) => r.data),
  composeTemplate: (data) =>
    client.post("/templates/compose", data).then((r) => r.data),
  composeTemplatePreview: (data) =>
    client
      .post("/templates/compose?preview=1", data, { responseType: "blob" })
      .then((r) => r.data),
  composeDocx: (file, name) => {
    const fd = new FormData();
    fd.append("file", file);
    if (name) fd.append("name", name);
    return client
      .post("/templates/compose-docx", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },

  // integration (Phase M)
  listApiKeys: () => client.get("/api-keys").then((r) => r.data),
  createApiKey: (data) => client.post("/api-keys", data).then((r) => r.data),
  revokeApiKey: (id) => client.delete(`/api-keys/${id}`).then((r) => r.data),
  getWebhook: () => client.get("/integration/webhook").then((r) => r.data),
  configureWebhook: (data) =>
    client.put("/integration/webhook", data).then((r) => r.data),
  deleteWebhook: () =>
    client.delete("/integration/webhook").then((r) => r.data),
  listWebhookDeliveries: (params = {}) =>
    client
      .get("/integration/webhook/deliveries", { params })
      .then((r) => r.data),

  // dashboard
  dashboardStats: () => client.get("/dashboard/stats").then((r) => r.data),
  dashboardMe: () => client.get("/dashboard/me").then((r) => r.data),
  dashboardOrg: () => client.get("/dashboard/org").then((r) => r.data),

  // reports (Phase I)
  reportWorkflows: (params = {}) =>
    client.get("/reports/workflows", { params }).then((r) => r.data),

  // search (Phase I)
  search: (q, type) =>
    client
      .get("/search", { params: type ? { q, type } : { q } })
      .then((r) => r.data),

  // seed
  seed: (reset = false) =>
    client.post(`/seed?reset=${reset}`).then((r) => r.data),
};

export const ROLE_LABELS = {
  owner: "Владелец",
  admin: "Администратор",
  designer: "Дизайнер процессов",
  operator: "Оператор",
  signer: "Подписант",
  auditor: "Аудитор",
  lawyer: "Юрист",
};

export const STATUS_LABELS = {
  draft: "Черновик",
  sent: "На подписи",
  signed: "Подписан",
  completed: "Завершён",
  rejected: "Отклонён",
  expired: "Истёк срок",
  voided: "Отозван",
};

export const STATUS_CLASS = {
  draft: "status-draft",
  sent: "status-sent",
  signed: "status-signed",
  completed: "status-completed",
  rejected: "status-rejected",
  expired: "status-rejected",
  voided: "status-draft",
};

export function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("ru-RU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function formatBytes(bytes) {
  if (!bytes) return "0 Б";
  const k = 1024;
  const sizes = ["Б", "КБ", "МБ", "ГБ"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

// UX-1: бэкенд отдаёт `detail.message` по-английски ("Invalid email or
// password"), и раньше он попадал в русский интерфейс как есть. Коды стабильны
// и являются частью контракта API — переводим по коду, а текст с сервера
// оставляем только как запасной вариант для неизвестных кодов.
const ERROR_MESSAGES_RU = {
  // Аутентификация
  INVALID_CREDENTIALS: "Неверный email или пароль",
  INVALID_REFRESH_TOKEN: "Сессия истекла. Войдите заново",
  PASSWORD_TOO_SHORT: "Пароль должен быть не короче 8 символов",
  INVALID_EMAIL: "Укажите корректный email",
  EMAIL_TAKEN: "Этот email уже зарегистрирован",
  // Подтверждение email кодом при регистрации
  EMAIL_NOT_VERIFIED: "Подтвердите email: запросите код и введите его",
  EMAIL_PROOF_INVALID: "Срок подтверждения истёк — запросите код заново",
  EMAIL_PROOF_MISMATCH: "Подтверждение выдано для другого адреса",
  INVALID_CODE: "Неверный или устаревший код",
  CODE_RESEND_TOO_SOON: "Код уже отправлен — повторите через минуту",
  EMAIL_MISMATCH: "Email не совпадает с приглашением",
  // Регистрация организации
  INVALID_ORG_NAME: "Укажите название организации",
  INVALID_BIN: "БИН должен содержать ровно 12 цифр",
  BIN_TAKEN: "Организация с таким БИН уже зарегистрирована",
  ORG_BIN_TAKEN: "Организация с таким БИН уже зарегистрирована",
  INVALID_SLUG: "Недопустимый адрес организации",
  ORG_SLUG_TAKEN: "Такой адрес организации уже занят",
  // Приглашения
  INVITATION_NOT_FOUND: "Приглашение не найдено",
  INVITATION_EXPIRED: "Срок действия приглашения истёк",
  INVITATION_REVOKED: "Приглашение отозвано",
  INVITATION_ALREADY_ACCEPTED: "Приглашение уже принято",
  ALREADY_A_MEMBER: "Вы уже состоите в этой организации",
  // Восстановление пароля и подтверждение почты
  INVALID_RESET_TOKEN: "Ссылка для сброса пароля недействительна",
  RESET_TOKEN_EXPIRED: "Срок действия ссылки истёк",
  RESET_TOKEN_USED: "Ссылка уже была использована",
  RESET_TOKEN_REVOKED: "Ссылка больше недействительна",
  INVALID_VERIFICATION_TOKEN: "Ссылка подтверждения недействительна",
  VERIFICATION_TOKEN_EXPIRED: "Срок действия ссылки подтверждения истёк",
  VERIFICATION_TOKEN_REVOKED: "Ссылка подтверждения больше недействительна",
  // Доступ
  INSUFFICIENT_ROLE: "Недостаточно прав для этого действия",
  FORBIDDEN_ORG: "Нет доступа к этой организации",
  MISSING_ORG: "Организация не выбрана",
  INVALID_ROLE: "Недопустимая роль",
  // ЭЦП / NCA Layer
  INVALID_ECP: "Не удалось проверить ЭЦП",
  ECP_INVALID: "Не удалось проверить ЭЦП",
  ECP_VERIFY_FAILED: "Проверка ЭЦП не пройдена",
  ECP_NO_SIGNER: "В сертификате не найден подписант",
  ECP_SIGNATURE_REQUIRED: "Требуется подпись ЭЦП",
  ECP_IDENTITY_MISMATCH: "ИИН в сертификате не совпадает с вашим профилем",
  ECP_CHALLENGE_INVALID: "Срок действия запроса на подпись истёк. Попробуйте снова",
  ECP_SERVICE_UNAVAILABLE: "Сервис ЭЦП недоступен. Попробуйте позже",
  INVALID_CERT_PASSWORD: "Неверный пароль контейнера ЭЦП",
  // Ресурсы
  NOT_FOUND: "Не найдено",
  DOCUMENT_NOT_FOUND: "Документ не найден",
  TEMPLATE_NOT_FOUND: "Шаблон не найден",
  WORKFLOW_NOT_FOUND: "Процесс не найден",
  FORM_NOT_FOUND: "Форма не найдена",
  ORG_NOT_FOUND: "Организация не найдена",
  RECIPIENT_ORG_NOT_FOUND: "Организация получателя не найдена",
  // Процессы и задачи
  WORKFLOW_NOT_PUBLISHABLE: "Процесс нельзя опубликовать: есть ошибки в схеме",
  STEP_NOT_PENDING: "Шаг уже обработан",
  REMARK_REQUIRED: "Укажите комментарий",
  CAMUNDA_UNAVAILABLE: "Движок процессов недоступен. Попробуйте позже",
  // Прочее
  INVALID_STATUS: "Недопустимый статус",
  INVALID_FILTER: "Недопустимый фильтр",
  INVALID_DATE: "Недопустимая дата",
  NO_DOCUMENT_DATA: "Нет данных документа",
};

export function errorMessage(err, fallback = "Произошла ошибка") {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object") {
    if (detail.code && ERROR_MESSAGES_RU[detail.code]) {
      return ERROR_MESSAGES_RU[detail.code];
    }
    if (detail.message) return detail.message;
  }
  if (typeof detail === "string") return detail;
  // Сеть/таймаут: axios отдаёт "Network Error" — тоже по-английски.
  if (err?.code === "ERR_NETWORK" || err?.message === "Network Error") {
    return "Нет связи с сервером. Проверьте подключение";
  }
  if (err?.message) return err.message;
  return fallback;
}
